import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardFooter,
  CardTitle,
} from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Star, MapPin, Clock, Phone, ShoppingCart, Share2, Heart, ChevronRight } from "lucide-react";
import { ProductGrid } from "@/components/ProductGrid";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import defaultBanner from "@/assets/banner-store/batik-parang-seamless.jpg";

// interface Store {
//   id: string;
//   name: string;
//   description: string;
//   address: string;
//   city: string;
//   phone: string;
//   operating_hours: string;
//   rating: number;
//   total_review: number;
//   joined_date: string;
//   image_url: string;
//   banner_url: string;
// }

// interface Product {
//   discount_price?: number;
//   sold: number;
//   rating: number;
// }

interface Store {
  id: string;
  name: string;
  banner_path: string;
  image_path: string;
  store_rating: number;
  store_total_review: number;
  store_joined_date: string;
  store_address: string;
  store_city: string;
  operating_hours: string;
  store_phone: number;
  description: string;
  created_at: string;
  updated_at: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  discount_price?: number;
  stock: number;
  category: string;
  image_url: string;
  is_active: boolean;
  created_at: string;
  store_id?: string;
  stores?: { name: string };
  sold: number;
  rating: number;
}

export const TokoPage = () => {
  const { id } = useParams();
  const [store, setStore] = useState<Store | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchStoreData = async () => {
      try {
        setLoading(true);
        
        // Fetch store details
        const { data: storeData, error: storeError } = await supabase
          .from("stores")
          .select("*")
          .eq("id", id)
          .single();

        if (storeError) throw storeError;
        if (!storeData) throw new Error("Toko tidak ditemukan");

        setStore(storeData as Store);

        // Fetch store products
        const { data: productsData, error: productsError } = await supabase
          .from("products")
          .select("*")
          .eq("store_id", id)
          .eq("is_active", true)
          .order("created_at", { ascending: false });

        if (productsError) throw productsError;

        setProducts(productsData.map(p => ({
          ...p,
          sold: 0,
          rating: 4.5,
          stock: p.stock || 0
        })));
      } catch (err) {
        console.error("Error fetching store data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchStoreData();
  }, [id]);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === "all" || product.category === activeTab;
    return matchesSearch && matchesTab;
  });

  const categories = [...new Set(products.map(product => product.category))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Banner Skeleton */}
          <Skeleton className="h-64 w-full rounded-2xl mb-8" />
          
          {/* Store Card Skeleton */}
          <Skeleton className="h-48 w-full rounded-2xl mb-8" />
          
          {/* Product Grid Skeleton */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-6">
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} className="h-64 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 shadow-independence border border-border/20">
          <CardHeader className="text-center">
            <CardTitle className="text-xl font-display">Toko Tidak Ditemukan</CardTitle>
            <CardDescription className="text-muted-foreground">
              Toko yang Anda cari tidak tersedia atau mungkin sudah tidak aktif
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              variant="outline" 
              className="w-full border-primary/20 hover:bg-primary/5" 
              onClick={() => window.history.back()}
            >
              Kembali
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Store Banner */}
      <div className="relative h-64 w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-independence" />
        <img 
          src={store.banner_path || defaultBanner} 
          alt={`Banner ${store.name}`}
          className="w-full h-full object-cover mix-blend-overlay opacity-80"
        />
        {/* {!store.banner_path && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold">{store.name.charAt(0)}</span>
              </div>
              <h1 className="text-3xl font-display font-bold drop-shadow-lg">{store.name}</h1>
              <p className="text-white/90 mt-2 font-medium">Toko Terpercaya</p>
            </div>
          </div>
        )} */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
      </div>

      {/* Store Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
        <div className="bg-card rounded-2xl shadow-independence border border-white/20 overflow-hidden">
          {/* Store Profile Section */}
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Store Avatar */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 rounded-2xl border-4 border-white bg-gradient-card overflow-hidden shadow-soft">
                  <img
                    src={store.image_path || "https://cdn0.iconfinder.com/data/icons/hr-business-and-finance/100/face_human_blank_user_avatar_mannequin_dummy-512.png"}
                    alt={store.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Store Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-2xl font-display font-bold text-foreground mb-2">{store.name}</h2>
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-accent text-accent" />
                        <span className="font-semibold text-foreground">
                          {store.store_rating ? store.store_rating.toFixed(1) : "4.9"}
                        </span>
                      </div>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground">
                        {store.store_total_review ? store.store_total_review : 10} ulasan
                      </span>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground">
                        Bergabung {new Date(store.store_joined_date ? store.store_joined_date : "2025-08-01").toLocaleDateString('id-ID', { year: 'numeric', month: 'long' })}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="gap-2 border-primary/20 hover:bg-primary/5">
                      <Share2 className="w-4 h-4" />
                      Bagikan
                    </Button>
                  </div>
                </div>

                {/* Store Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-card border border-primary/10">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm text-foreground mb-1">Lokasi Toko</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {store.store_address ? store.store_address : "Jl.Gayung Sari"}
                        {store.store_city ? `, ${store.store_city}` : " Surabaya"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-card border border-accent/10">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-accent" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm text-foreground mb-1">Jam Operasional</h3>
                      <p className="text-sm text-muted-foreground">{store.operating_hours || "09:00 - 21:00"}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-card border border-primary/10">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm text-foreground mb-1">Kontak</h3>
                      <p className="text-sm text-muted-foreground">{store.store_phone || "08123456789"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Navigation */}
        <div className="mt-8 bg-card rounded-2xl shadow-soft border border-white/20 overflow-hidden">
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="w-full justify-start bg-gradient-card border-b border-border/50 rounded-none h-16 px-6">
              <TabsTrigger 
                value="all" 
                className="px-6 h-12 rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-soft font-medium"
              >
                Semua Produk
              </TabsTrigger>
              {categories.map(category => (
                <TabsTrigger 
                  key={category} 
                  value={category} 
                  className="px-6 h-12 rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-soft font-medium"
                >
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Search and Filter */}
        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Cari produk di toko ini..."
              className="pl-12 h-12 rounded-xl border-border/50 bg-card shadow-soft focus:border-primary focus:shadow-independence transition-all"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button variant="outline" className="h-12 px-6 rounded-xl border-border/50 hover:bg-primary/5 hover:border-primary/20 shadow-soft">
            Filter
          </Button>
        </div>

        {/* Product Count */}
        <div className="mt-6 flex justify-between items-center p-4 bg-card rounded-xl border border-border/50 shadow-soft">
          <p className="text-sm font-medium text-foreground">
            Menampilkan <span className="text-primary font-bold">{filteredProducts.length}</span> produk
          </p>
          <div className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
            <span>Urutkan</span>
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>

        {/* Product Grid */}
        <div className="mt-6">
          {filteredProducts.length > 0 ? (
            <ProductGrid products={filteredProducts} className="grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-6" />
          ) : (
            <Card className="p-8 text-center bg-gradient-card border border-border/50 shadow-soft rounded-2xl">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-display">Tidak ada produk yang ditemukan</CardTitle>
                <CardDescription className="text-muted-foreground">
                  {searchQuery 
                    ? "Coba cari dengan kata kunci lain atau hapus filter"
                    : "Toko ini belum memiliki produk yang tersedia"}
                </CardDescription>
              </CardHeader>
            </Card>
          )}
        </div>

        {/* Store Footer */}
        <div className="mt-8 py-12 text-center">
          {/* <div className="bg-gradient-card rounded-2xl p-8 border border-border/50 shadow-soft">
            <h3 className="text-2xl font-display font-bold text-foreground mb-4">Tentang {store.name}</h3>
            <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {store.description || "Toko profesional dengan berbagai produk berkualitas tinggi. Kami berkomitmen memberikan pelayanan terbaik untuk semua pelanggan."}
            </p>
          </div> */}
        </div>
      </div>
    </div>
  );
};